/**
 * Gemeinsame Bausteine der Artikel- und Designverträge: Medien und Renderkontext für die Vorschau,
 * Medien-ID-Format und der Stand des Beschreibungsabgleichs. Eigene Datei, damit `articles.ts` und
 * `designs.ts` sie ohne Zyklus nutzen (ein Zyklus zwischen Schema-Modulen bricht je nach Ladereihenfolge).
 */
import { z } from 'zod';
import { EbaySiteId, Media, StoreCategories } from '@pat/core/entities';

/** Medium für die Vorschau (Bilder und Logo); URL = Medienbasis der Stage (`PAT_MEDIA_BASE_URL`, prod `https://h.patcdn.net/`) + `objectKey`. */
export const MediaView = Media.pick({ mediaId: true, objectKey: true, width: true, height: true, contentType: true }).extend({
  /**
   * Eigene Basis des Mediums. Mit eigenem Stage-Hosting (dev, prod seit 30.10.2026) trägt jedes Medium sie: Altmedien das
   * Legacy-Hosting, Uploads die Stage-Basis; die Kontextbasis ist dann das Legacy-Hosting. Jeder Weg, der eine `MediaView`
   * baut oder kopiert, muss sie mitnehmen.
   */
  baseUrl: z.string().regex(/^https:\/\/[a-z0-9.-]+(?::[0-9]+)?\/$/i).optional(),
});
export type MediaView = z.infer<typeof MediaView>;

/** Dieselben Shopdaten für Design-/Artikelvorschau und den kopierten HTML-Code. */
export const ShopNavigation = z.object({
  storeUrl: z.string().url().refine(value => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash;
    } catch { return false; }
  }),
  categories: StoreCategories.shape.categories,
});
export type ShopNavigation = z.infer<typeof ShopNavigation>;

/** Alles, was die Vorschau braucht: Design samt Medien und den Renderkontext des Mandanten. */
export const PreviewContext = z.object({
  hostingBaseUrl: z.string().url(),
  site: EbaySiteId,
  logoFree: z.boolean(),
  shopNavigation: ShopNavigation.optional(),
});
export type PreviewContext = z.infer<typeof PreviewContext>;

/** Medien-IDs landen in Schlüsseln (`MEDIA#<id>`): einfache Zeichen, kurz. */
export const MEDIA_ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Ob die Beschreibungen auf eBay aktualisiert werden. Die Absicht schreibt packages/data bei Veröffentlichen und
 * Zuordnen immer in die Outbox; der Zustand sagt nur, ob der Worker sie jetzt annähme: `requested` = ja;
 * `queued` = vorgemerkt, aber die Übertragung an eBay ist in dieser Stage noch nicht gebunden; `order-deferred` = wie
 * `order-required`, aber die Stage nimmt Aufträge an, bevor die Übertragung gebunden ist (nur dev, Pilot, Claude
 * 26.10.2026); `none` = kein aktives Angebot betroffen; sonst der Grund der Sperre. „Erneut senden“ schreibt nur bei `requested`.
 */
export const ReviseState = z.enum([
  'requested',
  'queued',
  // Vollständige Beschreibungen nur auf ausdrücklichen Auftrag je Angebot (Flori 25.10.2026): Veröffentlichen und
  // Zuordnen schreiben nichts Vollständiges, der Kunde beauftragt jedes Angebot einzeln.
  'order-required',
  'order-deferred',
  'none',
  'jobs-off',
  'paused',
  'live-update-off',
  // Live-Update ist ein Opt-in des Kunden (`Tenant.liveUpdateOptIn`), getrennt vom Tarif-Recht.
  'opt-in-off',
  'sync-incomplete',
  'ebay-disconnected',
  'account-inactive',
  'failed',
]);
export type ReviseState = z.infer<typeof ReviseState>;
